import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { api, resolveAssetUrl } from "@/lib/api";
import type { PageResponse } from "@/lib/pagination";
import { buildListQuery } from "@/lib/pagination";
import type { BioArtItem, BioArtCategory, BioArtMediaType } from "@/lib/types";
import Pagination from "@/components/base/Pagination";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useServerPagination } from "@/lib/useServerPagination";
import ImageUploadField from "@/components/feature/ImageUploadField";
import { isValidYouTubeUrl } from "@/lib/youtube";
import ContestPostersPanel from "./ContestPostersPanel";

type FormMode = "list" | "create" | "edit" | "bulk";
type AdminTab = "works" | "posters";

const YEARS = [2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013] as const;

const emptyForm = {
  title: "",
  categoryId: "" as string | number,
  year: 2021 as number,
  mediaType: "image" as BioArtMediaType,
  imageUrl: "",
  youtubeUrl: "",
  description: "",
  status: "active" as string,
  sortOrder: 0,
};

type BulkRow = {
  key: string;
  title: string;
  imageUrl: string;
  year: number;
  categoryId: string;
  description: string;
  uploading?: boolean;
  error?: string;
};

type UploadResult = { url: string; originalFilename?: string | null };

function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, "").trim() || name;
}

function sortFilesByName(files: File[]): File[] {
  return [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
  );
}

export default function AdminBioArt() {
  const [adminTab, setAdminTab] = useState<AdminTab>("works");
  const [categories, setCategories] = useState<BioArtCategory[]>([]);
  const [mode, setMode] = useState<FormMode>("list");
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [bulkYear, setBulkYear] = useState(2021);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkStatus, setBulkStatus] = useState("draft");
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCategories = useCallback(async () => {
    try {
      const catRes = await api.get<BioArtCategory[]>("/admin/bio-art/categories");
      if (catRes.data) setCategories(catRes.data);
    } catch (err: unknown) {
      showToast("카테고리 로딩 실패: " + (err instanceof Error ? err.message : "오류"));
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const list = useServerPagination<BioArtItem>({
    resetDeps: [search, filterCategory, filterYear],
    enabled: mode === "list" && adminTab === "works",
    fetchPage: useCallback(
      async (page, size) => {
        const res = await api.getPage<PageResponse<BioArtItem>>(
          "/admin/bio-art" +
            buildListQuery(page + 1, size, {
              search: search || undefined,
              categoryId: filterCategory || undefined,
              year: filterYear || undefined,
            })
        );
        return res.data;
      },
      [search, filterCategory, filterYear]
    ),
  });

  const handleDelete = async (id: number) => {
    if (!confirm("정말 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/admin/bio-art/${id}`);
      showToast("삭제되었습니다.");
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "삭제 실패");
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast("Title을 입력해주세요.");
      return;
    }
    if (!form.categoryId) {
      showToast("카테고리를 선택해주세요.");
      return;
    }
    if (form.mediaType === "image" && !form.imageUrl.trim()) {
      showToast("이미지를 등록해주세요.");
      return;
    }
    if (form.mediaType === "youtube") {
      if (!form.youtubeUrl.trim()) {
        showToast("유튜브 URL을 입력해주세요.");
        return;
      }
      if (!isValidYouTubeUrl(form.youtubeUrl)) {
        showToast("올바른 유튜브 URL을 입력해주세요.");
        return;
      }
    }

    const payload = {
      title: form.title.trim(),
      categoryId: Number(form.categoryId),
      year: form.year,
      mediaType: form.mediaType,
      imageUrl: form.mediaType === "image" ? form.imageUrl.trim() : null,
      youtubeUrl: form.mediaType === "youtube" ? form.youtubeUrl.trim() : null,
      description: form.description.trim() || null,
      status: form.status,
      sortOrder: form.sortOrder || 0,
    };

    setSaving(true);
    try {
      if (mode === "edit" && editId) {
        await api.put(`/admin/bio-art/${editId}`, payload);
        showToast("수정되었습니다.");
      } else {
        await api.post("/admin/bio-art", payload);
        showToast("생성되었습니다.");
      }
      setMode("list");
      setForm({ ...emptyForm });
      setEditId(null);
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item: BioArtItem) => {
    setEditId(item.id);
    setForm({
      title: item.title,
      categoryId: item.categoryId ?? "",
      year: item.year,
      mediaType: item.mediaType,
      imageUrl: item.imageUrl ?? "",
      youtubeUrl: item.youtubeUrl ?? "",
      description: item.description ?? "",
      status: item.status,
      sortOrder: item.sortOrder ?? 0,
    });
    setMode("edit");
  };

  const openBulk = () => {
    setBulkYear(2021);
    setBulkCategoryId(categories[0] ? String(categories[0].id) : "");
    setBulkStatus("draft");
    setBulkRows([]);
    setMode("bulk");
  };

  const uploadBulkFiles = async (files: FileList | File[]) => {
    const listFiles = sortFilesByName(
      Array.from(files).filter((f) => f.type.startsWith("image/"))
    );
    if (listFiles.length === 0) {
      showToast("이미지 파일만 업로드할 수 있습니다.");
      return;
    }

    setBulkUploading(true);
    const nextRows: BulkRow[] = [...bulkRows];

    for (const file of listFiles) {
      if (file.size > 5 * 1024 * 1024) {
        nextRows.push({
          key: `${file.name}-${Date.now()}-${Math.random()}`,
          title: titleFromFilename(file.name),
          imageUrl: "",
          year: bulkYear,
          categoryId: bulkCategoryId,
          description: "",
          error: "5MB 초과",
        });
        continue;
      }
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      try {
        const res = await api.upload<UploadResult>("/admin/files/images", file);
        if (res.data?.url) {
          nextRows.push({
            key,
            title: titleFromFilename(res.data.originalFilename?.trim() || file.name),
            imageUrl: res.data.url,
            year: bulkYear,
            categoryId: bulkCategoryId,
            description: "",
          });
        }
      } catch (err: unknown) {
        nextRows.push({
          key,
          title: titleFromFilename(file.name),
          imageUrl: "",
          year: bulkYear,
          categoryId: bulkCategoryId,
          description: "",
          error: err instanceof Error ? err.message : "업로드 실패",
        });
      }
    }

    setBulkRows(nextRows);
    setBulkUploading(false);
  };

  const handleBulkSave = async () => {
    if (!bulkCategoryId) {
      showToast("카테고리를 선택해주세요.");
      return;
    }
    const valid = bulkRows.filter((r) => r.imageUrl && r.title.trim() && !r.error);
    if (valid.length === 0) {
      showToast("등록할 이미지 항목이 없습니다.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/admin/bio-art/bulk", {
        year: bulkYear,
        categoryId: Number(bulkCategoryId),
        status: bulkStatus,
        items: valid.map((r, idx) => ({
          title: r.title.trim(),
          imageUrl: r.imageUrl,
          year: r.year,
          categoryId: r.categoryId ? Number(r.categoryId) : null,
          description: r.description.trim() || null,
          sortOrder: idx,
        })),
      });
      showToast(`${valid.length}건이 등록되었습니다.`);
      setMode("list");
      setBulkRows([]);
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "일괄 등록 실패");
    } finally {
      setSaving(false);
    }
  };

  const categoryName = (cid: number | null) => {
    if (!cid) return "—";
    return categories.find((c) => c.id === cid)?.name ?? "—";
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-[#2c4964] text-white text-sm rounded-full shadow-lg">
          {toast}
        </div>
      )}

      <div>
        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => {
              setAdminTab("works");
              setMode("list");
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors cursor-pointer ${
              adminTab === "works"
                ? "bg-[#1977cc] text-white border-[#1977cc]"
                : "bg-white text-[#2c4964] border-gray-200 hover:bg-gray-50"
            }`}
          >
            Works
          </button>
          <button
            type="button"
            onClick={() => {
              setAdminTab("posters");
              setMode("list");
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors cursor-pointer ${
              adminTab === "posters"
                ? "bg-[#1977cc] text-white border-[#1977cc]"
                : "bg-white text-[#2c4964] border-gray-200 hover:bg-gray-50"
            }`}
          >
            Contest Poster
          </button>
        </div>

        {adminTab === "posters" ? (
          <ContestPostersPanel onToast={showToast} />
        ) : mode === "list" && (
          <>
            <ScrollReveal>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      placeholder="Search title..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 w-full sm:w-56"
                    />
                  </div>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                  >
                    <option value="">All Years</option>
                    {YEARS.map((y) => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                  >
                    <option value="">All Categories</option>
                    {categories.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={openBulk}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#1977cc] text-sm font-medium rounded-md border border-[#1977cc]/40 hover:bg-[#f1f7fc] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-upload-2-line" /> Bulk Upload
                  </button>
                  <button
                    onClick={() => {
                      setMode("create");
                      setForm({ ...emptyForm, categoryId: categories[0]?.id ?? "" });
                      setEditId(null);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" /> Add Bio-Art
                  </button>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.05}>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f1f7fc]">
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">ID</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Title</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Year</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Category</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Media</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Status</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                    ) : list.items.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No items found.</td></tr>
                    ) : (
                      list.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{item.id}</td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-700 font-medium">{item.title}</td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-600">{item.year}</td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {categoryName(item.categoryId)}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-600">{item.mediaType}</td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              item.status === "active" ? "bg-green-50 text-green-700"
                              : item.status === "inactive" ? "bg-red-50 text-red-700"
                              : "bg-yellow-50 text-yellow-700"
                            }`}>{item.status}</span>
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button onClick={() => openEdit(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1977cc] hover:bg-[#f1f7fc] rounded transition-colors cursor-pointer">
                                <i className="ri-edit-line" /> Edit
                              </button>
                              <button onClick={() => handleDelete(item.id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer">
                                <i className="ri-delete-bin-line" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {!list.loading && (
                  <Pagination
                    page={list.page}
                    totalPages={list.totalPages}
                    totalItems={list.totalItems}
                    pageSize={list.pageSize}
                    rangeStart={list.rangeStart}
                    rangeEnd={list.rangeEnd}
                    onPageChange={list.setPage}
                    onPageSizeChange={list.setPageSize}
                    pageSizeOptions={list.pageSizeOptions}
                  />
                )}
              </div>
            </ScrollReveal>
          </>
        )}

        {adminTab === "works" && (mode === "create" || mode === "edit") && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#2c4964]">
                  {mode === "create" ? "Add Bio-Art" : "Edit Bio-Art"}
                </h2>
                <button
                  onClick={() => { setMode("list"); setForm({ ...emptyForm }); setEditId(null); }}
                  className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
                    placeholder="Title"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                      Year <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.year}
                      onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                    >
                      {YEARS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={String(form.categoryId)}
                      onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                    >
                      <option value="">— Select —</option>
                      {categories.map((c) => (
                        <option key={c.id} value={String(c.id)}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                    >
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="draft">draft</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Sort Order</label>
                    <input
                      type="number"
                      min={0}
                      value={form.sortOrder}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
                    />
                    <p className="mt-1 text-xs text-gray-400">숫자가 작을수록 먼저 표시됩니다.</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] resize-none"
                    placeholder="Optional description"
                  />
                  <p className="mt-1 text-xs text-gray-400 text-right">{form.description.length}/500</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                    Media Type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    {(["image", "youtube"] as BioArtMediaType[]).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            mediaType: type,
                            imageUrl: type === "image" ? f.imageUrl : "",
                            youtubeUrl: type === "youtube" ? f.youtubeUrl : "",
                          }))
                        }
                        className={`px-4 py-2 text-sm rounded-md border cursor-pointer transition-colors ${
                          form.mediaType === type
                            ? "bg-[#1977cc] text-white border-[#1977cc]"
                            : "bg-white text-[#2c4964] border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {type === "image" ? "Image" : "YouTube"}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">이미지와 유튜브는 둘 중 하나만 등록할 수 있습니다.</p>
                </div>

                {form.mediaType === "image" ? (
                  <ImageUploadField
                    label="Image"
                    value={form.imageUrl}
                    onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
                  />
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                      YouTube URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={form.youtubeUrl}
                      onChange={(e) => setForm((f) => ({ ...f, youtubeUrl: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc]"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={() => { setMode("list"); setForm({ ...emptyForm }); setEditId(null); }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {mode === "create" ? "Create" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {adminTab === "works" && mode === "bulk" && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-[#2c4964]">Bulk Upload</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    여러 이미지를 선택하면 Title은 파일명으로 자동 설정됩니다. 업로드 후 개별 수정이 가능합니다.
                  </p>
                </div>
                <button
                  onClick={() => { setMode("list"); setBulkRows([]); }}
                  className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Default Year</label>
                  <select
                    value={bulkYear}
                    onChange={(e) => setBulkYear(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white"
                  >
                    {YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Default Category</label>
                  <select
                    value={bulkCategoryId}
                    onChange={(e) => setBulkCategoryId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white"
                  >
                    <option value="">— Select —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Status</label>
                  <select
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white"
                  >
                    <option value="draft">draft</option>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
              </div>

              <input
                ref={bulkInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    void uploadBulkFiles(e.target.files);
                    e.target.value = "";
                  }
                }}
              />

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files?.length) void uploadBulkFiles(e.dataTransfer.files);
                }}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center mb-6 hover:border-[#1977cc]/40 transition-colors"
              >
                <i className="ri-image-add-line text-3xl text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 mb-3">이미지를 드래그하거나 클릭하여 다중 선택</p>
                <button
                  type="button"
                  disabled={bulkUploading}
                  onClick={() => bulkInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm rounded-md hover:bg-[#1669b3] cursor-pointer disabled:opacity-60"
                >
                  <i className="ri-upload-2-line" />
                  {bulkUploading ? "Uploading..." : "Select Images"}
                </button>
              </div>

              {bulkRows.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-gray-600">
                      {bulkRows.filter((r) => r.imageUrl).length} / {bulkRows.length} ready
                    </p>
                    <button
                      type="button"
                      onClick={() => setBulkRows([])}
                      className="text-xs text-red-500 hover:underline cursor-pointer"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 max-h-[480px] overflow-y-auto pr-1">
                    {bulkRows.map((row) => (
                      <div key={row.key} className="border border-gray-200 rounded-lg p-3">
                        <div className="h-28 bg-[#f6f9fc] rounded mb-2 overflow-hidden flex items-center justify-center">
                          {row.imageUrl ? (
                            <img src={resolveAssetUrl(row.imageUrl)} alt={row.title} className="max-h-full max-w-full object-contain" />
                          ) : (
                            <span className="text-xs text-red-400">{row.error || "No image"}</span>
                          )}
                        </div>
                        <input
                          type="text"
                          value={row.title}
                          onChange={(e) =>
                            setBulkRows((rows) =>
                              rows.map((r) => (r.key === row.key ? { ...r, title: e.target.value } : r))
                            )
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded mb-2"
                          placeholder="Title"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={row.year}
                            onChange={(e) =>
                              setBulkRows((rows) =>
                                rows.map((r) =>
                                  r.key === row.key ? { ...r, year: Number(e.target.value) } : r
                                )
                              )
                            }
                            className="px-2 py-1.5 text-xs border border-gray-200 rounded bg-white"
                          >
                            {YEARS.map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                          <select
                            value={row.categoryId}
                            onChange={(e) =>
                              setBulkRows((rows) =>
                                rows.map((r) =>
                                  r.key === row.key ? { ...r, categoryId: e.target.value } : r
                                )
                              )
                            }
                            className="px-2 py-1.5 text-xs border border-gray-200 rounded bg-white"
                          >
                            {categories.map((c) => (
                              <option key={c.id} value={String(c.id)}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                        <textarea
                          value={row.description}
                          onChange={(e) =>
                            setBulkRows((rows) =>
                              rows.map((r) =>
                                r.key === row.key ? { ...r, description: e.target.value } : r
                              )
                            )
                          }
                          rows={2}
                          maxLength={500}
                          className="w-full mt-2 px-2 py-1.5 text-xs border border-gray-200 rounded resize-none"
                          placeholder="Description"
                        />
                        <button
                          type="button"
                          onClick={() => setBulkRows((rows) => rows.filter((r) => r.key !== row.key))}
                          className="mt-2 text-xs text-red-500 hover:underline cursor-pointer"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => { setMode("list"); setBulkRows([]); }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkSave}
                  disabled={saving || bulkUploading || bulkRows.filter((r) => r.imageUrl).length === 0}
                  className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] cursor-pointer disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save All"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}
